/**
 * Phase 3 of amenity reservations (#640) from the household's side:
 * deposits, the fee broken into lines, pricing rules, the waitlist, access
 * codes and required documents.
 *
 * Pure functions over the generated types only — a type import, never the
 * client's runtime, which pulls in axios as ESM and Jest cannot load it.
 * Booking times are the association's wall clock (`*_local`) and never go
 * through `new Date(string)`; money stays a decimal string throughout.
 */
import type {
  AmenityPriceRuleReturn,
  AmenityReturn,
  ReservationDocumentRequirementStatus,
  ReservationFeeLine,
  ReservationReturn,
  ReservationWaitlistReturn,
} from '../clients/rust-api-client';
import { formatMoney } from '../utils/billingHelpers';
import {
  compareLocal,
  formatClock,
  formatDuration,
  formatLocalDate,
  formatLocalDateTime,
  formatLocalRange,
  formatLocalTime,
  isFreeAmount,
} from './reservationFormat';

// ---------------------------------------------------------------------------
// Fee lines and pricing
// ---------------------------------------------------------------------------

/**
 * Whether the fee needs its lines shown: more than one, or one that is not
 * the plain base fee (a weekend rate replacing it, say). A single base line
 * is just "the fee" and says nothing the total does not.
 */
export const showFeeBreakdown = (lines?: ReservationFeeLine[] | null): boolean =>
  !!lines && (lines.length > 1 || lines.some((l) => l.kind !== 'base'));

/**
 * What an offered waitlist time costs, as the server priced it for the
 * offered time and party (#640 phase 3) — the fee, its lines when they
 * say more than the total, and any deposit. Null while nothing is offered
 * or the server sent no price, so the card says nothing rather than guess.
 */
export interface OfferPrice {
  fee: string;
  lines: ReservationFeeLine[];
  deposit: string | null;
}

export const offerPrice = (
  e: Pick<ReservationWaitlistReturn, 'status' | 'offered_fee' | 'offered_fee_breakdown' | 'offered_deposit'>
): OfferPrice | null => {
  if (e.status !== 'offered' || e.offered_fee === null || e.offered_fee === undefined) return null;
  return {
    fee: isFreeAmount(e.offered_fee) ? 'Free' : formatMoney(e.offered_fee),
    lines: showFeeBreakdown(e.offered_fee_breakdown) ? e.offered_fee_breakdown : [],
    deposit:
      e.offered_deposit && !isFreeAmount(e.offered_deposit)
        ? `${formatMoney(e.offered_deposit)} refundable deposit, charged with the fee`
        : null,
  };
};

/** `Free` or `$150.00` — one fee line's amount. */
export const feeLineAmount = (line: Pick<ReservationFeeLine, 'amount'>): string =>
  isFreeAmount(line.amount) ? 'Free' : formatMoney(line.amount);

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * `Fri–Sun`, `Mon, Wed, Fri`, `Every day` from a rule's weekdays
 * (0 = Sunday, the backend's convention). Runs of three or more
 * consecutive days are a range; a run that wraps the week (Fri–Sun with
 * Sunday as 0) is read across the wrap.
 */
export const weekdaysText = (weekdays: number[]): string => {
  const days = Array.from(new Set(weekdays.map((d) => ((d % 7) + 7) % 7))).sort(
    (a, b) => a - b
  );
  if (days.length === 7) return 'Every day';
  if (days.length === 0) return '';
  // Start the walk just after a gap, so a run across Saturday→Sunday stays whole.
  const has = (d: number): boolean => days.indexOf(((d % 7) + 7) % 7) !== -1;
  let start = days.find((d) => !has(d - 1));
  if (start === undefined) start = days[0];
  const runs: number[][] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = (start + i) % 7;
    if (!has(d)) continue;
    const last = runs[runs.length - 1];
    if (last && (last[last.length - 1] + 1) % 7 === d) last.push(d);
    else runs.push([d]);
  }
  return runs
    .map((run) =>
      run.length >= 3
        ? `${WEEKDAYS_SHORT[run[0]]}–${WEEKDAYS_SHORT[run[run.length - 1]]}`
        : run.map((d) => WEEKDAYS_SHORT[d]).join(', ')
    )
    .join(', ');
};

const per = (feeType: string): string => {
  switch (feeType) {
    case 'hourly':
      return 'an hour';
    case 'per_night':
      return 'a night';
    default:
      return 'per booking';
  }
};

/**
 * One pricing rule as a homeowner reads it:
 * `Weekend rate — Fri–Sun, from 5:00 PM: $200.00 per booking`.
 */
export const priceRuleText = (
  rule: Pick<
    AmenityPriceRuleReturn,
    'name' | 'weekdays' | 'start_minute' | 'end_minute' | 'fee_type' | 'amount'
  >
): string => {
  const days = weekdaysText(rule.weekdays);
  const allDay = rule.start_minute === 0 && (rule.end_minute === null || rule.end_minute === undefined);
  const time = allDay
    ? ''
    : rule.end_minute === null || rule.end_minute === undefined
      ? `, from ${formatClock(rule.start_minute)}`
      : `, ${formatClock(rule.start_minute)} – ${formatClock(rule.end_minute)}`;
  const price = isFreeAmount(rule.amount) ? 'free' : `${formatMoney(rule.amount)} ${per(rule.fee_type)}`;
  return `${rule.name} — ${days}${time}: ${price}`;
};

/**
 * Extra-guest pricing, or null when there is none:
 * `Up to 10 guests included; $5.00 for each one after that.`
 */
export const guestPricingText = (
  a: Pick<AmenityReturn, 'included_guests' | 'guest_fee_per_person'>
): string | null => {
  if (!a.guest_fee_per_person || isFreeAmount(a.guest_fee_per_person)) return null;
  const fee = formatMoney(a.guest_fee_per_person);
  const included = a.included_guests ?? 0;
  if (included <= 0) return `${fee} for each person.`;
  return `Up to ${included === 1 ? '1 person' : `${included} people`} included; ${fee} for each one after that.`;
};

// ---------------------------------------------------------------------------
// Deposits
// ---------------------------------------------------------------------------

/** The amenity's refundable deposit, said before booking; null if none. */
export const depositPolicyText = (
  a: Pick<AmenityReturn, 'deposit_amount' | 'booking_mode'>
): string | null => {
  if (!a.deposit_amount || isFreeAmount(a.deposit_amount)) return null;
  const what = a.booking_mode === 'overnight' ? 'stay' : 'booking';
  return `A ${formatMoney(a.deposit_amount)} refundable deposit goes on your account with the booking and comes back after your ${what}, unless something needs repair or cleaning.`;
};

/**
 * What has happened to a booking's deposit, in the household's words —
 * null when the booking carries none. A deduction always comes with the
 * reason management gave, which the backend requires.
 */
export const depositStatusText = (
  r: Pick<
    ReservationReturn,
    | 'deposit_amount'
    | 'deposit_status'
    | 'deposit_deduction_amount'
    | 'deposit_deduction_reason'
    | 'status'
  >
): string | null => {
  if (!r.deposit_amount || isFreeAmount(r.deposit_amount)) return null;
  const amount = formatMoney(r.deposit_amount);
  const kept =
    r.deposit_deduction_amount && !isFreeAmount(r.deposit_deduction_amount)
      ? formatMoney(r.deposit_deduction_amount)
      : null;
  const reason = r.deposit_deduction_reason ? ` (${r.deposit_deduction_reason})` : '';
  switch (r.deposit_status) {
    case 'held':
      return `${amount} deposit held — returned after your booking unless something needs repair or cleaning.`;
    case 'released':
      return `${amount} deposit returned.`;
    case 'partially_released':
      return `${kept ?? 'Part'} of the ${amount} deposit kept${reason}; the rest returned.`;
    case 'forfeited':
      return `${amount} deposit kept${reason}.`;
    case 'refund_due':
      return kept
        ? `${kept} of the ${amount} deposit kept${reason}; your management company is refunding the rest.`
        : `${amount} deposit — your management company is arranging the refund.`;
    case 'credited':
      return kept
        ? `${kept} of the ${amount} deposit kept${reason}; the rest returned to your account as a credit.`
        : `${amount} deposit returned to your account as a credit.`;
    case 'refunded':
      return kept
        ? `${kept} of the ${amount} deposit kept${reason}; the rest returned — your management company is sending a refund.`
        : `${amount} deposit returned — your management company is sending a refund.`;
    case 'none':
    default:
      return r.status === 'pending'
        ? `${amount} refundable deposit, charged only if approved.`
        : `${amount} refundable deposit — not charged.`;
  }
};

/** The deposit line on the review step, from a preview; null if none. */
export const depositReviewText = (
  deposit: string | null | undefined,
  pending: boolean
): string | null => {
  if (!deposit || isFreeAmount(deposit)) return null;
  return pending
    ? `${formatMoney(deposit)} refundable, charged to your account only if approved`
    : `${formatMoney(deposit)} refundable, charged to your account`;
};

/**
 * A start time in a group's grid: `2:00 PM · 2 of 3 free`, or just
 * `2:00 PM` when the group has one amenity.
 */
export const groupStartLabel = (time: string, free: number, total: number): string =>
  total <= 1 ? time : `${time} · ${free} of ${total} free`;

// ---------------------------------------------------------------------------
// Waitlist
// ---------------------------------------------------------------------------

/** `2 hr 15 min`, `45 min`, `less than a minute` — seconds left on an offer. */
export const formatCountdown = (seconds: number): string => {
  if (seconds < 60) return 'less than a minute';
  const totalMinutes = Math.floor(seconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
};

/** `Sat, Oct 3, 2026, between 2:00 PM and 6:00 PM · 2 hours`. */
export const waitlistWindowText = (
  e: Pick<
    ReservationWaitlistReturn,
    'earliest_start_local' | 'latest_end_local' | 'duration_minutes'
  >
): string =>
  `${formatLocalDate(e.earliest_start_local)}, between ${formatLocalTime(e.earliest_start_local)} and ${formatLocalTime(e.latest_end_local)} · ${formatDuration(e.duration_minutes)}`;

export interface WaitlistState {
  label: string;
  detail: string;
  /** An offer the household can still take. */
  live: boolean;
}

/**
 * Where a waitlist entry stands. `nowSeconds` is the epoch now; an offer's
 * expiry is an epoch instant from the server, so this is the one place a
 * real clock is compared rather than the association's wall clock.
 */
export const waitlistState = (
  e: Pick<
    ReservationWaitlistReturn,
    'status' | 'position' | 'offered_start_local' | 'offered_end_local' | 'offer_expires_at'
  >,
  nowSeconds: number
): WaitlistState => {
  switch (e.status) {
    case 'waiting':
      return {
        label: 'Waiting',
        detail:
          e.position && e.position > 0
            ? `You're number ${e.position} in line. We'll let you know if a time frees up.`
            : "We'll let you know if a time frees up.",
        live: false,
      };
    case 'offered': {
      const when =
        e.offered_start_local && e.offered_end_local
          ? formatLocalRange(e.offered_start_local, e.offered_end_local)
          : 'A time';
      const left = e.offer_expires_at ? e.offer_expires_at - nowSeconds : null;
      if (left !== null && left <= 0) {
        return { label: 'Offer ended', detail: `${when} was held for you, but the hold has ended.`, live: false };
      }
      return {
        label: 'A time is yours to take',
        detail:
          left !== null
            ? `${when} is held for you for ${formatCountdown(left)}. Accept it to book, or pass so the next household can.`
            : `${when} is held for you. Accept it to book, or pass so the next household can.`,
        live: true,
      };
    }
    case 'accepted':
      return { label: 'Booked', detail: 'You took the offered time.', live: false };
    case 'expired':
      return { label: 'Offer lapsed', detail: 'The offered time was passed to the next household.', live: false };
    case 'declined':
      return { label: 'Passed', detail: 'You passed on the offered time.', live: false };
    case 'withdrawn':
    default:
      return { label: 'Withdrawn', detail: 'You left the waitlist.', live: false };
  }
};

/** `2026-10-03T14:00:00` from a date input and a time input. */
export const localFromInputs = (date: string, time: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  return `${date}T${time}:00`;
};

// ---------------------------------------------------------------------------
// Access
// ---------------------------------------------------------------------------

export type AccessView =
  | { kind: 'shown'; code: string | null; instructions: string | null }
  | { kind: 'later'; from: string }
  | { kind: 'none' };

/**
 * What the household sees about getting in. The server decides the window
 * — it sends the code and instructions only while the booking is approved
 * and inside `[start − reveal hours, end)` — so this only words it.
 */
export const accessView = (
  r: Pick<
    ReservationReturn,
    'status' | 'access_code' | 'access_instructions' | 'access_visible_from_local'
  >
): AccessView => {
  if (r.access_code || r.access_instructions) {
    return {
      kind: 'shown',
      code: r.access_code ?? null,
      instructions: r.access_instructions ?? null,
    };
  }
  if (r.status === 'approved' && r.access_visible_from_local) {
    return { kind: 'later', from: r.access_visible_from_local };
  }
  return { kind: 'none' };
};

/**
 * `Oct 3, 2:04 PM` — an epoch instant (a check-in), shown on the
 * association's clock. Unlike a booking's `*_local` times this is a real
 * moment, so putting it through the zone is right.
 */
export const formatEpochTime = (seconds: number, timeZone: string): string => {
  try {
    return new Date(seconds * 1000).toLocaleString('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return new Date(seconds * 1000).toLocaleString('en-US');
  }
};

/** `Your access details appear here from Fri, Oct 2, 2026 at 2:00 PM.` */
export const accessLaterText = (from: string): string =>
  `Your access code and directions appear here from ${formatLocalDateTime(from)}.`;

// ---------------------------------------------------------------------------
// Required documents
// ---------------------------------------------------------------------------

export type DocumentTone = 'green' | 'amber' | 'red' | 'zinc';

/** Where one required document stands, and what to do about it. */
export const documentState = (
  s: Pick<ReservationDocumentRequirementStatus, 'satisfied' | 'due_on' | 'latest'>,
  today: string
): { tone: DocumentTone; text: string } => {
  if (s.satisfied) return { tone: 'green', text: 'Accepted.' };
  const latest = s.latest;
  if (latest?.status === 'submitted') {
    return { tone: 'zinc', text: `${latest.file_name} sent — waiting for your management company to review it.` };
  }
  if (latest?.status === 'rejected') {
    return {
      tone: 'red',
      text: `Not accepted${latest.review_note ? `: ${latest.review_note}` : ''}. Please upload a new one.`,
    };
  }
  if (latest?.status === 'accepted') {
    // Accepted, but not enough: it runs out before the booking.
    return { tone: 'red', text: 'The one on file expires before your booking. Please upload a current one.' };
  }
  const overdue = compareLocal(today, s.due_on) > 0;
  return {
    tone: overdue ? 'red' : 'amber',
    text: overdue
      ? `Was due ${formatLocalDate(s.due_on)}. Please upload it as soon as you can.`
      : `Due by ${formatLocalDate(s.due_on)}.`,
  };
};

/** What the upload control accepts — the backend's upload allowlist. */
export const DOCUMENT_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx,.txt,application/pdf,image/*';

/** Required documents, said before booking; empty when there are none. */
export const documentRequirementLines = (
  a: Pick<AmenityReturn, 'document_requirements'>
): string[] =>
  (a.document_requirements ?? [])
    .filter((r) => r.is_active)
    .map((r) => {
      const ahead =
        r.due_days_before_start > 0
          ? `, at least ${r.due_days_before_start === 1 ? '1 day' : `${r.due_days_before_start} days`} before your booking`
          : '';
      return `${r.title}${ahead}${r.requires_expiry ? ' (with its expiry date)' : ''}.`;
    });
